import { 
  doc, 
  getDoc 
} from 'firebase/firestore';
import { db } from '../../firebaseConfig';
import { COLLECTIONS } from './constants';
import { WorkflowDocument } from '../../types/workflowTypes';
import { safeNumber } from './utils/dataUtils';

/**
 * Service for document-related operations
 */
export class DocumentService {
  /**
   * Loads document values directly from Firestore collections
   * @param documents - Documents whose values should be updated
   * @returns Updated documents with correct values
   */
  static async loadDocumentValues(documents: WorkflowDocument[]): Promise<WorkflowDocument[]> {
    if (!documents || documents.length === 0) return documents;
    
    // Copy documents so we can safely modify them
    const updatedDocuments = [...documents];
    
    try {
      // Check for permission issues from previous attempts
      const permissionDeniedKey = 'permission_denied_documents';
      const hasPermissionDenied = sessionStorage.getItem(permissionDeniedKey);
      
      if (hasPermissionDenied === 'true') {
        if (process.env.NODE_ENV === 'development') {
          console.log('[DocumentService] Access previously denied, skipping direct loading');
        }
        
        // Use existing values, but ensure they are correctly converted
        return updatedDocuments.map(doc => ({
          ...doc,
          totalAmount: safeNumber(doc.totalAmount)
        }));
      }
      
      // Process each document
      for (let i = 0; i < updatedDocuments.length; i++) {
        const docItem = updatedDocuments[i];
        
        try {
          if (docItem.type === 'invoice') {
            // Try to load the invoice
            const invoiceDocRef = doc(db, COLLECTIONS.INVOICES, docItem.id);
            const invoiceDocSnap = await getDoc(invoiceDocRef);
            
            if (invoiceDocSnap.exists()) {
              const invoiceData: any = invoiceDocSnap.data();
              
              // Extract amount from invoice
              if (invoiceData?.totalAmount !== undefined && invoiceData?.totalAmount !== null) {
                const amount = safeNumber(invoiceData.totalAmount);
                
                // Update document with new amount, even if it's 0
                updatedDocuments[i] = {
                  ...docItem,
                  totalAmount: amount
                };
              } else if (invoiceData?.items && Array.isArray(invoiceData.items)) {
                // If no totalAmount, but items are present, calculate total from items
                const calculatedTotal = invoiceData.items.reduce((sum: number, item: any) => {
                  const quantity = safeNumber(item.quantity) || 1;
                  const price = safeNumber(item.netPrice) || safeNumber(item.price) || 0;
                  return sum + (quantity * price);
                }, 0);
                
                if (calculatedTotal > 0) {
                  updatedDocuments[i] = {
                    ...docItem,
                    totalAmount: calculatedTotal
                  };
                }
              }
            }
          } else if (docItem.type === 'offer') {
            // Try to load the offer
            const offerDocRef = doc(db, COLLECTIONS.OFFERS, docItem.id);
            const offerDocSnap = await getDoc(offerDocRef);
            
            if (offerDocSnap.exists()) {
              const offerData: any = offerDocSnap.data();
              
              // Extract amount from offer
              if (offerData?.totalAmount !== undefined && offerData?.totalAmount !== null) {
                const amount = safeNumber(offerData.totalAmount);
                
                // Update document with new amount, even if it's 0
                updatedDocuments[i] = {
                  ...docItem,
                  totalAmount: amount
                };
              } else if (offerData?.items && Array.isArray(offerData.items)) {
                // If no totalAmount, but items are present, calculate total from items
                const calculatedTotal = offerData.items.reduce((sum: number, item: any) => {
                  const quantity = safeNumber(item.quantity) || 1;
                  const price = safeNumber(item.netPrice) || safeNumber(item.price) || 0;
                  return sum + (quantity * price);
                }, 0);
                
                if (calculatedTotal > 0) {
                  updatedDocuments[i] = {
                    ...docItem,
                    totalAmount: calculatedTotal
                  };
                }
              }
            }
          }
        } catch (error: any) {
          // For permission errors, warn once and remember for the session
          if (error && error.code === 'permission-denied') {
            sessionStorage.setItem(permissionDeniedKey, 'true');
            console.warn('[DocumentService] Permission error loading documents - skipping further attempts');
            
            // For permission errors, still ensure the existing values are correctly converted
            return updatedDocuments.map(doc => ({
              ...doc,
              totalAmount: safeNumber(doc.totalAmount)
            }));
          } else if (process.env.NODE_ENV === 'development') {
            console.warn(`[DocumentService] Error loading document ${docItem.id}:`, error);
          }
        }
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[DocumentService] Error trying to load document values:', error);
      }
    }
    
    // Ensure all totalAmount values are correctly formatted as numbers
    const result = updatedDocuments.map(doc => ({
      ...doc,
      totalAmount: typeof doc.totalAmount === 'number' && !isNaN(doc.totalAmount) 
        ? doc.totalAmount 
        : safeNumber(doc.totalAmount)
    }));
    
    return result;
  }
} 